import axios from "axios";
import React, { useEffect, useState } from "react";
import { CornerDownLeft, Edit, Link, Search, Trash2, Eye } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Input,
  Button,
  Table,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import {
  getInventorySupplier,
  getInventoryPurchase,
} from "../../../../../redux/inventory";
import { useNavigate } from "react-router-dom";
import PurchaseForm from "./PurchaseForm";
import PaginatedList from "../PaginatedList";

const Purchase = () => {
  const itemsList = useSelector((state) => state.itemsList);
  const dispatch = useDispatch();
  const { supplierData = [], purchaseData, itemsData } = itemsList;
  const auth = useSelector((state) => state.authentication);
  const { userData } = auth;

  const [formData, setFormData] = useState("");
  const [modal, setModal] = useState(false);
  const [modalItems, setModalItems] = useState(false);
  const [selectedInvoiceItems, setSelectedInvoiceItems] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();
  
  const toggle = () => setModal(!modal);
  const toggleModalItems = () => setModalItems(!modalItems);
  
  const [mode, setMode] = useState("");
  const [purchasePaginatedList, setPurchasePaginatedList] = useState([]);
  const [showSpinner, setShowSpinner] = useState(false);

  // Fetch Suppliers and Purchases on load
  useEffect(() => {
    if (userData?.token && userData?.branch) {
      dispatch(
        getInventorySupplier({
          token: userData.token,
          branch: userData.branch,
        })
      );
      dispatch(
        getInventoryPurchase({
          token: userData.token,
          branch: userData.branch,
        })
      );
    }
  }, [dispatch, userData?.token, userData?.branch]);

  const editSupplierHandler = (Data, toggleModal, type) => {
    setMode(type);
    setFormData(Data);
    toggleModal();
  };

  const deleteHandler = (item, itemId) => {
    const slno = prompt("Please enter the SL No. to confirm delete");

    if (slno && Number(slno.trim()) === itemId) {
      axios
        .delete(`${process.env.REACT_APP_CRI_API}delete-purchase`, {
          params: { id: item._id, branch: userData.branch },
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        })
        .then((res) => {
          if (res) {
            alert("Purchase Entry Deleted Successfully");
            dispatch(
              getInventoryPurchase({
                token: userData.token,
                branch: userData.branch,
              })
            );
          }
        })
        .catch((err) => {
          if (
            (err.response?.data?.err?.name === "TokenExpiredError") ||
            err.response?.data?.message === "Unauthorized"
          ) {
            sessionStorage.clear();
            navigate("/cri-crm");
          } else {
            alert("Failed to delete purchase entry");
          }
        });
    } else if (slno) {
      alert("Entered SL No. does not match");
    }
  };

  const supplierNameHandler = (supplierId) => {
    if (!supplierData || !supplierId) return "N/A";
    const supplier = supplierData.find(
      (item) => item.supplierId === supplierId
    );
    return supplier ? supplier.supplierName : "N/A";
  };

  const handleViewItems = (invoiceData) => {
    if (!invoiceData) {
      setSelectedInvoiceItems([]);
      return;
    }
    const itemsArray = Array.isArray(invoiceData)
      ? invoiceData
      : Object.values(invoiceData);

    const parsedItems = itemsArray.map((row) => {
      if (row && typeof row === "object") {
        const innerKey = Object.keys(row)[0];
        return innerKey ? row[innerKey] : row;
      }
      return row;
    }).filter((row) => row && row.item);

    setSelectedInvoiceItems(parsedItems);
    toggleModalItems();
  };

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3">
        <Col md={2}>
          <Button
            color="primary"
            block
            onClick={() => {
              setFormData("");
              setMode("New Purchase");
              toggle();
            }}
          >
            + Purchase Entry
          </Button>
        </Col>
        <Col md={4}>
          <InputGroup>
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by Supplier Name..."
            />
            <InputGroupText>
              <Search size={16} />
            </InputGroupText>
          </InputGroup>
        </Col>
      </Row>

      <PaginatedList
        setShowSpinner={setShowSpinner}
        endPoint2="purchase-page"
        setPurchasePaginatedList={setPurchasePaginatedList}
        purchaseLimit={100}
      />

      {!purchasePaginatedList?.length ? (
        <div className="text-center p-4">No Purchase Data Available</div>
      ) : (
        <Table responsive striped bordered hover className="bg-white align-middle">
          <thead className="table-secondary">
            <tr>
              <th style={{ width: "60px" }} className="text-center">Sl No.</th>
              <th style={{ width: "20%" }}>Supplier Name</th>
              <th>Invoice Date</th>
              <th>Invoice No</th>
              <th>Amount Paid</th>
              <th>Payment Mode</th>
              <th className="text-center">Items</th>
              <th className="text-center">Return</th>
              <th className="text-center">Edit</th>
              <th className="text-center">Delete</th>
            </tr>
          </thead>
          <tbody>
            {purchasePaginatedList
              .filter((item) => {
                if (!searchInput) return true;
                const name = supplierNameHandler(item.supplier?.supplierId);
                return name?.toLowerCase().includes(searchInput.toLowerCase());
              })
              .map((item, id) => {
                return (
                  <tr key={item._id || id}>
                    <td className="text-center">{id + 1}</td>
                    <td>{supplierNameHandler(item.supplier?.supplierId)}</td>
                    <td>
                      {item.paymentDetails?.invoice_date
                        ? new Date(item.paymentDetails.invoice_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>{item.paymentDetails?.invoice_id || "-"}</td>
                    <td>₹{item.paymentDetails?.amount_paid || "0"}</td>
                    <td>{item.paymentDetails?.payment_mode || "-"}</td>
                    <td className="text-center">
                      <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        onClick={() => handleViewItems(item.invoice)}
                      >
                        <Eye size={18} color="#0d6efd" />
                      </Button>
                    </td>
                    <td className="text-center">
                      <CornerDownLeft
                        color="#0d6efd"
                        style={{ cursor: "pointer" }}
                        onClick={() => editSupplierHandler(item, toggle, "Return Mode")}
                      />
                    </td>
                    <td className="text-center">
                      {(userData.rights === "Admin" ||
                        userData.rights === "Manager" ||
                        userData.rights === "Accounts") && (
                        <Edit
                          color="#198754"
                          style={{ cursor: "pointer" }}
                          onClick={() => editSupplierHandler(item, toggle, "Edit Mode")}
                        />
                      )}
                    </td>
                    <td className="text-center">
                      {(userData.rights === "Admin" ||
                        userData.rights === "Manager" ||
                        userData.rights === "Accounts") && (
                        <Trash2
                          color="#dc3545"
                          style={{ cursor: "pointer" }}
                          onClick={() => deleteHandler(item, id + 1)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      )}

      {/* Main Form Entry/Edit Modal */}
      <Modal isOpen={modal} toggle={toggle} fullscreen>
        <ModalHeader toggle={toggle}>{mode || "Purchase Entry"}</ModalHeader>
        <ModalBody>
          <PurchaseForm
            editFormData={formData}
            togglePurchaseEntry={toggle}
            mode={mode}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Detailed Items View Modal */}
      <Modal isOpen={modalItems} toggle={toggleModalItems} size="lg">
        <ModalHeader toggle={toggleModalItems}>Purchased Items</ModalHeader>
        <ModalBody>
          <Table bordered responsive striped>
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Item Name</th>
                <th>Unit Price</th>
                <th>Qty</th>
                <th>MRP</th>
                <th>Batch</th>
              </tr>
            </thead>
            <tbody>
              {selectedInvoiceItems.length > 0 ? (
                selectedInvoiceItems.map((item, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{item.item || "-"}</td>
                    <td>₹{item.unitPrice || 0}</td>
                    <td>{item.quantity || 0}</td>
                    <td>₹{item.mrp || 0}</td>
                    <td>{item.batch || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No items found for this purchase invoice.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModalItems}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Purchase;